/**
 * Currency formatting — shared across ops screens.
 *
 * Every monetary value in the system is integer cents. These helpers render
 * cents as USD *with thousands separators* via Intl.NumberFormat, replacing
 * the per-screen `$${(c / 100).toFixed(2)}` definitions that produced
 * unseparated strings like "$12345.67".
 */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Intl formatters are expensive to construct; one per currency, built lazily. */
const byCurrency = new Map<string, Intl.NumberFormat>([['USD', USD]])

function formatterFor(currency: string): Intl.NumberFormat {
  const code = currency.toUpperCase()
  let f = byCurrency.get(code)
  if (!f) {
    try {
      f = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: code,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    } catch {
      f = USD // unknown/malformed code from an upstream payload — don't throw mid-render
    }
    byCurrency.set(code, f)
  }
  return f
}

/**
 * Cents → "$12,345.67".
 *
 * `currency` is only needed by Stripe-backed screens (disputes can settle in a
 * non-USD currency); everything else in the system is USD and can omit it.
 */
export function fmtMoney(cents: number, currency = 'USD'): string {
  return formatterFor(currency).format((cents || 0) / 100)
}

/**
 * DOLLARS → "$12,345.67".
 *
 * The API exposes a few amounts as dollar floats rather than integer cents —
 * the `*_usd` fields on a session (`net_usd`, `gross_usd`, `refunded_usd`) and
 * the refund-modal amounts. Those call sites need a formatter that does NOT
 * divide by 100, and passing dollars to `fmtMoney` would silently render 1/100th
 * of the real amount on a refund confirmation. Keeping the unit in the function
 * name is what stops that swap from happening by accident.
 */
export function fmtUsdAmount(dollars: number): string {
  return USD.format(dollars || 0)
}

/**
 * A dollars text input → integer CENTS (or null for "no amount entered").
 *
 * Operators type dollars; every store in the system is cents. #464 was exactly
 * this boundary going the wrong way — a flat validation discount entered as "5"
 * was stored as 5 cents, so "$5 off" became "5¢ off". The rounding is the other
 * half: `19.99 * 100` is 1998.9999999999998 in IEEE-754, and a bare multiply
 * would ship that float to an API that (correctly) rejects non-integer cents.
 *
 * Returns null for empty/whitespace/unparseable input so a caller can tell
 * "cleared the field" (→ send null, clearing the stored price) apart from
 * "typed 0" (→ send 0, an explicitly comped permit). Negatives clamp to null:
 * there's no such thing as a negative permit price, and silently flipping the
 * sign would be worse.
 */
export function dollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, '').replace(/,/g, '')
  if (!trimmed) return null
  const dollars = Number(trimmed)
  if (!Number.isFinite(dollars) || dollars < 0) return null
  // `dollars * 100` alone is not enough: 19.99 * 100 is 1998.9999999999998 (fine
  // once rounded) but 1.005 * 100 is 100.49999999999999, which rounds DOWN and
  // loses a half cent. Snapping to two decimals first puts the value back on the
  // cent grid, so the round is a true half-up.
  return Math.round(Number((dollars * 100).toFixed(2)))
}

/**
 * Integer CENTS → the plain dollars string a text input should show ("125.00").
 *
 * The inverse of `dollarsToCents` for populating an edit form. Deliberately NOT
 * `fmtMoney`: a "$1,250.00" in an <input> round-trips back through Number() as
 * NaN, so the editable representation has to stay unformatted.
 */
export function centsToDollarsInput(cents: number | undefined | null): string {
  if (cents === undefined || cents === null) return ''
  return (cents / 100).toFixed(2)
}

/**
 * Signed cents → "+$12,345.67" / "−$12,345.67" / "$0.00".
 *
 * Uses a real minus sign (−, U+2212) rather than a hyphen so negatives read
 * cleanly and align under `font-variant-numeric: tabular-nums`. Mirrors the
 * old per-screen `fmtSigned` semantics (no parens; explicit + on positives).
 */
export function fmtMoneySigned(cents: number): string {
  const c = cents || 0
  if (c === 0) return USD.format(0)
  const abs = USD.format(Math.abs(c) / 100)
  return c < 0 ? `−${abs}` : `+${abs}`
}
